import { CloudArrowUpIcon as UploadIcon } from '@heroicons/react/24/outline';
import Dragger from 'antd/es/upload/Dragger';

import type { UploadedFile } from 'validators/createKeyModal';

import { DocumentIcon } from '@atoms/Icon/DocumentIcon';
import { TrashIcon } from '@atoms/Icon/TrashIcon';

import {
  CREATE_KEY_UPLOAD_BROWSE,
  CREATE_KEY_UPLOAD_DRAG_PREFIX,
  CREATE_KEY_UPLOAD_REMOVE_TEST_ID,
  CREATE_KEY_UPLOAD_TEST_ID,
  KILOBYTE,
  uploadedFilesLabel
} from './constants';

type CertificateUploadProps = {
  heading: string;
  formatsLabel: string;
  accept: string;
  upload: UploadedFile | null;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < KILOBYTE) {
    return `${bytes} B`;
  }
  return `${(bytes / KILOBYTE).toFixed(1)} KB`;
};

export const CertificateUpload = ({
  heading,
  formatsLabel,
  accept,
  upload,
  onFileSelected,
  onRemove
}: CertificateUploadProps) => (
  <div data-testid={CREATE_KEY_UPLOAD_TEST_ID} className="flex flex-col gap-2">
    <p className="text-sm font-medium leading-[18px] text-content-primary">
      {heading}
    </p>

    <Dragger
      name="file"
      className="upload-dragger w-full"
      multiple={false}
      showUploadList={false}
      accept={accept}
      beforeUpload={(file) => {
        onFileSelected(file as File);
        return false;
      }}
    >
      <div className="flex flex-col items-center justify-center py-3">
        <UploadIcon className="size-8 stroke-current text-content-secondary" />
        <div className="mt-2 text-sm text-content-primary">
          {CREATE_KEY_UPLOAD_DRAG_PREFIX}
          <span className="link-underline cursor-pointer hover:no-underline">
            {CREATE_KEY_UPLOAD_BROWSE}
          </span>
        </div>
        <div className="mt-1 text-xs text-content-secondary">
          {formatsLabel}
        </div>
      </div>
    </Dragger>

    <span className="text-sm leading-[18px] text-content-strong">
      {uploadedFilesLabel(upload ? 1 : 0)}
    </span>

    {upload && (
      <div className="flex items-center justify-between gap-3 rounded-full border border-border-default p-3">
        <div className="flex min-w-0 items-center gap-2">
          <DocumentIcon className="shrink-0 fill-feedback-info" />
          <span className="truncate text-sm font-medium leading-[18px] text-content-primary">
            {upload.name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-xs leading-[14px] text-content-strong">
            {formatFileSize(upload.size)}
          </span>
          <button
            type="button"
            data-testid={CREATE_KEY_UPLOAD_REMOVE_TEST_ID}
            aria-label={`Remove ${upload.name}`}
            onClick={onRemove}
            className="group cursor-pointer"
          >
            <TrashIcon className="size-[18px] stroke-content-secondary group-hover:stroke-feedback-error" />
          </button>
        </div>
      </div>
    )}
  </div>
);
